import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  computeMonthlyStats,
  filterTransactions,
  getMyWallet,
  getWalletTransactions,
  topupWallet,
  type TopupPayload,
  type TransactionFilter,
} from "../services/walletService";

export const WALLET_QUERY_KEY    = ["wallet"]                  as const;
export const WALLET_TX_QUERY_KEY = ["wallet", "transactions"] as const;

export function useMyWallet() {
  return useQuery({
    queryKey:  WALLET_QUERY_KEY,
    queryFn:   getMyWallet,
    staleTime: 30_000,
  });
}

// `filter` est appliqué côté client (la list endpoint n'en propose pas).
// Le filter est inclus dans la queryKey pour éviter toute collision de cache
// entre vues qui demandent un sous-ensemble différent.
export function useWalletTransactions(filter: TransactionFilter = "all") {
  return useQuery({
    queryKey: [...WALLET_TX_QUERY_KEY, filter] as const,
    queryFn:  getWalletTransactions,
    staleTime: 30_000,
    select:   (txs) => filterTransactions(txs, filter),
  });
}

// Stats partagent intentionnellement le fetch sous-jacent (même endpoint) mais
// possèdent leur propre clé pour éviter d'écraser `staleTime`.
export function useWalletStats() {
  return useQuery({
    queryKey:  [...WALLET_TX_QUERY_KEY, "stats"] as const,
    queryFn:   getWalletTransactions,
    staleTime: 60_000,
    select:    computeMonthlyStats,
  });
}

export function useTopupWallet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: TopupPayload) => topupWallet(payload),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: WALLET_QUERY_KEY });
      qc.invalidateQueries({ queryKey: WALLET_TX_QUERY_KEY });
    },
  });
}

export type { TransactionFilter };
